import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactHero from './components/ContactHero';
import ContactForm from './components/ContactForm';
import TeamDirectory from './components/TeamDirectory';
import OfficeLocation from './components/OfficeLocation';
import ResourceDownloads from './components/ResourceDownloads';
import LanguageSwitcher from './components/LanguageSwitcher';
import Icon from '../../components/AppIcon';

const Contact = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  // Load saved language preference
  useEffect(() => {
    const savedLanguage = localStorage.getItem('rexfel_language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  // Save language preference
  const handleLanguageChange = (langCode) => {
    setCurrentLanguage(langCode);
    localStorage.setItem('rexfel_language', langCode);
  };

  // Translations object
  const translations = {
    en: {
      meta: {
        title: "Contact REXFEL - Partnership Gateway | Strategic Investment Firm Hamburg",
        description: "Connect with REXFEL's investment team. Schedule meetings, download partnership resources, and submit investment inquiries. Hamburg-based strategic investment firm with global reach."
      },
      hero: {
        badge: "Partnership Gateway",
        title: "Let\'s Build the Future Together",
        description: "Connect with our investment team to explore strategic partnerships, discuss funding opportunities, and access exclusive resources for scaling your business.",
        stats: {
          response: "Response Time",
          languages: "Languages",
          invested: "Invested"
        },
        cta: {
          schedule: "Schedule Meeting",
          download: "Download Resources"
        },
        cards: {
          partnership: {
            title: "Strategic Partnership",
            subtitle: "Long-term collaboration opportunities"
          },
          investment: {
            title: "Investment Inquiry",
            subtitle: "Funding and growth capital"
          },
          general: {
            title: "General Inquiry",
            subtitle: "Questions and information"
          }
        }
      },
      form: {
        title: "Partnership Inquiry Form",
        subtitle: "Tell us about your business and how we can help you grow",
        fields: {
          inquiryType: {
            label: "Type of Inquiry",
            description: "Select the most relevant option for your request",
            placeholder: "Choose inquiry type..."
          },
          firstName: {
            label: "First Name",
            placeholder: "Enter your first name"
          },
          lastName: {
            label: "Last Name", 
            placeholder: "Enter your last name"
          },
          email: {
            label: "Email Address",
            placeholder: "your.email@company.com"
          },
          company: {
            label: "Company Name",
            placeholder: "Your company name"
          },
          position: {
            label: "Position/Title",
            placeholder: "CEO, Founder, etc."
          },
          phone: {
            label: "Phone Number",
            placeholder: "+49 40 123 456 789"
          },
          fundingStage: {
            label: "Funding Stage",
            placeholder: "Select current stage..."
          },
          industry: {
            label: "Industry Sector",
            placeholder: "Select industry..."
          },
          investmentAmount: {
            label: "Investment Amount Sought",
            placeholder: "€500K - €5M",
            description: "Approximate funding amount you\'re seeking"
          },
          businessPlan: {
            label: "Business Plan / Pitch Deck",
            placeholder: "Click to upload or drag and drop",
            description: "PDF, DOC, PPT up to 10MB"
          },
          message: {
            label: "Message",
            placeholder: "Tell us about your business, growth plans, and how REXFEL can help you succeed..."
          },
          newsletter: {
            label: "Subscribe to REXFEL newsletter for market insights and investment updates"
          },
          gdprConsent: {
            label: "I agree to the processing of my personal data according to REXFEL\'s Privacy Policy and GDPR regulations"
          }
        },
        inquiryTypes: {
          partnership: "Strategic Partnership",
          investment: "Investment Inquiry",
          general: "General Inquiry",
          media: "Media & Press"
        },
        fundingStages: {
          preSeed: "Pre-Seed",
          seed: "Seed",
          seriesA: "Series A",
          seriesB: "Series B+",
          growth: "Growth Stage"
        },
        industries: {
          other: "Other"
        },
        investmentSection: {
          title: "Investment Details"
        },
        submit: "Send Inquiry",
        submitting: "Sending...",
        success: {
          title: "Thank You for Your Inquiry!",
          message: "We\'ve received your partnership request and will review it carefully. Our team will respond within 24 hours.",
          nextSteps: {
            title: "Next Steps:",
            description: "1. Our investment team will review your submission\n2. We'll schedule an initial consultation call\n3. If there's mutual interest, we'll arrange a detailed presentation"
          }
        }
      },
      team: {
        title: "Meet Our Investment Team",
        subtitle: "Connect directly with our experienced professionals who specialize in strategic investments and partnership development.",
        members: {
          marcus: {
            position: "Managing Partner",
            department: "Investment Strategy",
            expertise: "20+ years in venture capital and strategic investments across European markets. Specializes in FinTech, SaaS, and digital transformation initiatives.",
            bio: "Dr. Marcus Weber leads REXFEL's investment strategy with over two decades of experience in venture capital. He has successfully guided 50+ companies through growth phases, with a particular focus on European FinTech and SaaS ventures. Marcus holds a PhD in Economics from Hamburg University and previously served as Partner at leading European VC firms.",
            availability: "Available for meetings"
          },
          sarah: {
            position: "Investment Director",
            department: "Due Diligence & Analysis",
            expertise: "Expert in financial modeling, market analysis, and investment due diligence. Former McKinsey consultant with deep expertise in digital business models.",
            bio: "Sarah Chen brings analytical rigor to REXFEL's investment process. With her background at McKinsey & Company and experience in investment banking, she leads our due diligence efforts and financial analysis. Sarah has evaluated over 200 investment opportunities and has a proven track record in identifying high-growth potential companies.",
            availability: "Available this week"
          },
          thomas: {
            position: "Partnership Director",
            department: "Business Development",
            expertise: "Specializes in strategic partnerships, business development, and international market expansion. Former startup founder with successful exit experience.",
            bio: "Thomas Müller combines entrepreneurial experience with strategic partnership expertise. As a former startup founder who successfully exited his e-commerce platform, he understands the challenges entrepreneurs face. Thomas now helps REXFEL portfolio companies scale through strategic partnerships and market expansion initiatives.",
            availability: "Available for calls"
          },
          elena: {
            position: "Legal & Compliance Director",
            department: "Legal Affairs",
            expertise: "International corporate law, regulatory compliance, and investment structuring. Specialized in European investment regulations and cross-border transactions.",
            bio: "Elena Rodriguez ensures all REXFEL investments comply with European regulations and international standards. With her multilingual capabilities and deep understanding of cross-border investment law, she facilitates smooth transaction processes and ongoing compliance for our portfolio companies.",
            availability: "Available next week"
          }
        },
        actions: {
          email: "Send Email",
          schedule: "Schedule Meeting",
          viewDetails: "View Details"
        },
        modal: {
          bio: "Biography",
          expertise: "Expertise",
          contact: "Contact Information"
        }
      },
      office: {
        title: "Visit Our Hamburg Office",
        subtitle: "Located in the heart of Hamburg's financial district, our office is easily accessible and equipped for productive meetings.",
        tabs: {
          map: "Location Map",
          directions: "Directions"
        },
        address: {
          title: "Office Address"
        },
        contact: {
          title: "Contact Information",
          main: "Main Office",
          general: "General Inquiries",
          reception: "Reception"
        },
        hours: {
          title: "Office Hours",
          monday: "Monday",
          tuesday: "Tuesday", 
          wednesday: "Wednesday",
          thursday: "Thursday",
          friday: "Friday",
          saturday: "Saturday",
          sunday: "Sunday",
          closed: "Closed"
        },
        status: {
          open: "Currently Open"
        },
        transportation: {
          title: "Public Transportation",
          parking: "Parking Available"
        },
        actions: {
          getDirections: "Get Directions"
        },
        visit: {
          title: "Planning a Visit?",
          description: "Please schedule an appointment in advance. Our reception team will assist with visitor access and meeting room arrangements."
        }
      },
      resources: {
        title: "Partnership Resources",
        subtitle: "Download comprehensive guides, application forms, and market insights to help you prepare for partnership with REXFEL.",
        search: {
          placeholder: "Search resources..."
        },
        categories: {
          all: "All Resources",
          partnership: "Partnership",
          investment: "Investment",
          application: "Application",
          research: "Research"
        },
        items: {
          partnershipGuide: {
            title: "Partnership Guidelines 2024",
            description: "Comprehensive guide covering REXFEL's partnership criteria, application process, and what to expect during the evaluation phase."
          },
          investmentCriteria: {
            title: "Investment Criteria & Focus Areas",
            description: "Detailed overview of our investment thesis, target industries, funding stages, and evaluation criteria for potential portfolio companies."
          },
          applicationForm: {
            title: "Partnership Application Template",
            description: "Structured application form to help you present your business opportunity in the format our investment team prefers."
          },
          marketReport: {
            title: "European FinTech Market Report 2024",
            description: "In-depth analysis of the European FinTech landscape, emerging trends, and investment opportunities in the digital finance sector."
          },
          dueDiligence: {
            title: "Due Diligence Checklist",
            description: "Complete checklist of documents and information required during the due diligence process to streamline your application."
          },
          portfolioOverview: {
            title: "Portfolio Company Success Stories",
            description: "Case studies and success stories from our portfolio companies, showcasing growth trajectories and strategic value creation."
          }
        },
        download: "Download",
        downloading: "Downloading...",
        downloads: "downloads",
        downloadSuccess: "downloaded successfully!",
        lastUpdated: "Last updated",
        noResults: {
          title: "No Resources Found",
          description: "Try adjusting your search terms or category filter.",
          reset: "Reset Filters"
        },
        notice: {
          title: "Resource Access Notice",
          description: "All resources are provided for informational purposes. By downloading, you agree to use these materials in accordance with our terms of service and privacy policy.",
          features: {
            gdpr: "GDPR Compliant",
            multilingual: "Multi-language Support",
            updated: "Regularly Updated"
          }
        }
      }
    },
    de: {
      meta: {
        title: "Kontakt REXFEL - Partnership Gateway | Strategische Investmentfirma Hamburg",
        description: "Verbinden Sie sich mit REXFELs Investment-Team. Terminvereinbarung, Partnership-Ressourcen herunterladen und Investment-Anfragen stellen. Hamburg-basierte strategische Investmentfirma mit globaler Reichweite."
      },
      hero: {
        badge: "Partnership Gateway",
        title: "Gemeinsam die Zukunft gestalten",
        description: "Verbinden Sie sich mit unserem Investment-Team, um strategische Partnerschaften zu erkunden, Finanzierungsmöglichkeiten zu besprechen und exklusive Ressourcen für das Wachstum Ihres Unternehmens zu erhalten.",
        stats: {
          response: "Antwortzeit",
          languages: "Sprachen",
          invested: "Investiert"
        },
        cta: {
          schedule: "Termin vereinbaren",
          download: "Ressourcen herunterladen"
        },
        cards: {
          partnership: {
            title: "Strategische Partnerschaft",
            subtitle: "Langfristige Kooperationsmöglichkeiten"
          },
          investment: {
            title: "Investment-Anfrage",
            subtitle: "Finanzierung und Wachstumskapital"
          },
          general: {
            title: "Allgemeine Anfrage",
            subtitle: "Fragen und Informationen"
          }
        }
      },
      form: {
        title: "Partnership-Anfrageformular",
        subtitle: "Erzählen Sie uns von Ihrem Unternehmen und wie wir Ihnen beim Wachstum helfen können",
        fields: {
          inquiryType: {
            label: "Art der Anfrage",
            description: "Wählen Sie die relevanteste Option für Ihre Anfrage",
            placeholder: "Anfragetyp wählen..."
          },
          firstName: {
            label: "Vorname",
            placeholder: "Ihren Vornamen eingeben"
          },
          lastName: {
            label: "Nachname",
            placeholder: "Ihren Nachnamen eingeben"
          },
          email: {
            label: "E-Mail-Adresse",
            placeholder: "ihre.email@unternehmen.de"
          },
          company: {
            label: "Firmenname",
            placeholder: "Ihr Firmenname"
          },
          position: {
            label: "Position/Titel",
            placeholder: "CEO, Gründer, etc."
          },
          phone: {
            label: "Telefonnummer",
            placeholder: "+49 40 123 456 789"
          },
          fundingStage: {
            label: "Finanzierungsphase",
            placeholder: "Aktuelle Phase wählen..."
          },
          industry: {
            label: "Branche",
            placeholder: "Branche wählen..."
          },
          investmentAmount: {
            label: "Gewünschter Investitionsbetrag",
            placeholder: "€500K - €5M",
            description: "Ungefährer Finanzierungsbetrag, den Sie suchen"
          },
          businessPlan: {
            label: "Businessplan / Pitch Deck",
            placeholder: "Klicken zum Hochladen oder Drag & Drop",
            description: "PDF, DOC, PPT bis zu 10MB"
          },
          message: {
            label: "Nachricht",
            placeholder: "Erzählen Sie uns von Ihrem Unternehmen, Wachstumsplänen und wie REXFEL Ihnen zum Erfolg verhelfen kann..."
          },
          newsletter: {
            label: "REXFEL Newsletter für Markteinblicke und Investment-Updates abonnieren"
          },
          gdprConsent: {
            label: "Ich stimme der Verarbeitung meiner persönlichen Daten gemäß REXFELs Datenschutzrichtlinie und DSGVO-Bestimmungen zu"
          }
        },
        inquiryTypes: {
          partnership: "Strategische Partnerschaft",
          investment: "Investment-Anfrage",
          general: "Allgemeine Anfrage",
          media: "Medien & Presse"
        },
        fundingStages: {
          preSeed: "Pre-Seed",
          seed: "Seed",
          seriesA: "Serie A",
          seriesB: "Serie B+",
          growth: "Wachstumsphase"
        },
        industries: {
          other: "Andere"
        },
        investmentSection: {
          title: "Investment-Details"
        },
        submit: "Anfrage senden",
        submitting: "Wird gesendet...",
        success: {
          title: "Vielen Dank für Ihre Anfrage!",
          message: "Wir haben Ihre Partnership-Anfrage erhalten und werden sie sorgfältig prüfen. Unser Team wird innerhalb von 24 Stunden antworten.",
          nextSteps: {
            title: "Nächste Schritte:",
            description: "1. Unser Investment-Team wird Ihre Einreichung prüfen\n2. Wir vereinbaren ein erstes Beratungsgespräch\n3. Bei gegenseitigem Interesse arrangieren wir eine detaillierte Präsentation"
          }
        }
      },
      team: {
        title: "Lernen Sie unser Investment-Team kennen",
        subtitle: "Verbinden Sie sich direkt mit unseren erfahrenen Fachleuten, die sich auf strategische Investitionen und Partnership-Entwicklung spezialisiert haben.",
        members: {
          marcus: {
            position: "Managing Partner",
            department: "Investment-Strategie",
            expertise: "20+ Jahre in Venture Capital und strategischen Investitionen in europäischen Märkten. Spezialisiert auf FinTech, SaaS und digitale Transformationsinitiativen.",
            bio: "Dr. Marcus Weber leitet REXFELs Investment-Strategie mit über zwei Jahrzehnten Erfahrung im Venture Capital. Er hat erfolgreich 50+ Unternehmen durch Wachstumsphasen geführt, mit besonderem Fokus auf europäische FinTech- und SaaS-Ventures. Marcus hat einen PhD in Wirtschaftswissenschaften von der Universität Hamburg und war zuvor Partner bei führenden europäischen VC-Firmen.",
            availability: "Verfügbar für Termine"
          },
          sarah: {
            position: "Investment Director",
            department: "Due Diligence & Analyse",
            expertise: "Expertin in Finanzmodellierung, Marktanalyse und Investment Due Diligence. Ehemalige McKinsey-Beraterin mit tiefgreifender Expertise in digitalen Geschäftsmodellen.",
            bio: "Sarah Chen bringt analytische Strenge in REXFELs Investment-Prozess ein. Mit ihrem Hintergrund bei McKinsey & Company und Erfahrung im Investment Banking leitet sie unsere Due-Diligence-Bemühungen und Finanzanalysen. Sarah hat über 200 Investment-Möglichkeiten bewertet und hat eine bewährte Erfolgsbilanz bei der Identifizierung von Unternehmen mit hohem Wachstumspotenzial.",
            availability: "Diese Woche verfügbar"
          },
          thomas: {
            position: "Partnership Director",
            department: "Business Development",
            expertise: "Spezialisiert auf strategische Partnerschaften, Business Development und internationale Marktexpansion. Ehemaliger Startup-Gründer mit erfolgreicher Exit-Erfahrung.",
            bio: "Thomas Müller kombiniert unternehmerische Erfahrung mit strategischer Partnership-Expertise. Als ehemaliger Startup-Gründer, der seine E-Commerce-Plattform erfolgreich verkauft hat, versteht er die Herausforderungen, denen Unternehmer gegenüberstehen. Thomas hilft nun REXFEL-Portfolio-Unternehmen beim Skalieren durch strategische Partnerschaften und Marktexpansionsinitiativen.",
            availability: "Verfügbar für Anrufe"
          },
          elena: {
            position: "Legal & Compliance Director",
            department: "Rechtsangelegenheiten",
            expertise: "Internationales Unternehmensrecht, regulatorische Compliance und Investment-Strukturierung. Spezialisiert auf europäische Investment-Vorschriften und grenzüberschreitende Transaktionen.",
            bio: "Elena Rodriguez stellt sicher, dass alle REXFEL-Investitionen den europäischen Vorschriften und internationalen Standards entsprechen. Mit ihren mehrsprachigen Fähigkeiten und ihrem tiefen Verständnis für grenzüberschreitendes Investment-Recht erleichtert sie reibungslose Transaktionsprozesse und laufende Compliance für unsere Portfolio-Unternehmen.",
            availability: "Nächste Woche verfügbar"
          }
        },
        actions: {
          email: "E-Mail senden",
          schedule: "Termin vereinbaren",
          viewDetails: "Details anzeigen"
        },
        modal: {
          bio: "Biografie",
          expertise: "Expertise",
          contact: "Kontaktinformationen"
        }
      },
      office: {
        title: "Besuchen Sie unser Hamburger Büro",
        subtitle: "Im Herzen von Hamburgs Finanzviertel gelegen, ist unser Büro leicht erreichbar und für produktive Meetings ausgestattet.",
        tabs: {
          map: "Standortkarte",
          directions: "Wegbeschreibung"
        },
        address: {
          title: "Büroadresse"
        },
        contact: {
          title: "Kontaktinformationen",
          main: "Hauptbüro",
          general: "Allgemeine Anfragen",
          reception: "Empfang"
        },
        hours: {
          title: "Bürozeiten",
          monday: "Montag",
          tuesday: "Dienstag",
          wednesday: "Mittwoch",
          thursday: "Donnerstag",
          friday: "Freitag",
          saturday: "Samstag",
          sunday: "Sonntag",
          closed: "Geschlossen"
        },
        status: {
          open: "Derzeit geöffnet"
        },
        transportation: {
          title: "Öffentliche Verkehrsmittel",
          parking: "Parkplätze verfügbar"
        },
        actions: {
          getDirections: "Wegbeschreibung abrufen"
        },
        visit: {
          title: "Planen Sie einen Besuch?",
          description: "Bitte vereinbaren Sie im Voraus einen Termin. Unser Empfangsteam hilft bei Besucherzugang und Meetingraum-Arrangements."
        }
      },
      resources: {
        title: "Partnership-Ressourcen",
        subtitle: "Laden Sie umfassende Leitfäden, Antragsformulare und Markteinblicke herunter, um sich auf eine Partnerschaft mit REXFEL vorzubereiten.",
        search: {
          placeholder: "Ressourcen durchsuchen..."
        },
        categories: {
          all: "Alle Ressourcen",
          partnership: "Partnerschaft",
          investment: "Investment",
          application: "Antrag",
          research: "Forschung"
        },
        items: {
          partnershipGuide: {
            title: "Partnership-Richtlinien 2024",
            description: "Umfassender Leitfaden zu REXFELs Partnership-Kriterien, Bewerbungsprozess und was während der Bewertungsphase zu erwarten ist."
          },
          investmentCriteria: {
            title: "Investment-Kriterien & Schwerpunktbereiche",
            description: "Detaillierter Überblick über unsere Investment-These, Zielbranchen, Finanzierungsphasen und Bewertungskriterien für potenzielle Portfolio-Unternehmen."
          },
          applicationForm: {
            title: "Partnership-Antragsvorlage",
            description: "Strukturiertes Antragsformular, um Ihre Geschäftsmöglichkeit in dem Format zu präsentieren, das unser Investment-Team bevorzugt."
          },
          marketReport: {
            title: "Europäischer FinTech-Marktbericht 2024",
            description: "Tiefgreifende Analyse der europäischen FinTech-Landschaft, aufkommende Trends und Investment-Möglichkeiten im digitalen Finanzsektor."
          },
          dueDiligence: {
            title: "Due-Diligence-Checkliste",
            description: "Vollständige Checkliste der Dokumente und Informationen, die während des Due-Diligence-Prozesses erforderlich sind, um Ihre Bewerbung zu optimieren."
          },
          portfolioOverview: {
            title: "Portfolio-Unternehmen Erfolgsgeschichten",
            description: "Fallstudien und Erfolgsgeschichten unserer Portfolio-Unternehmen, die Wachstumstrajektorien und strategische Wertschöpfung zeigen."
          }
        },
        download: "Herunterladen",
        downloading: "Wird heruntergeladen...",
        downloads: "Downloads",
        downloadSuccess: "erfolgreich heruntergeladen!",
        lastUpdated: "Zuletzt aktualisiert",
        noResults: {
          title: "Keine Ressourcen gefunden",
          description: "Versuchen Sie, Ihre Suchbegriffe oder Kategoriefilter anzupassen.",
          reset: "Filter zurücksetzen"
        },
        notice: {
          title: "Ressourcen-Zugangshinweis",
          description: "Alle Ressourcen werden zu Informationszwecken bereitgestellt. Durch das Herunterladen stimmen Sie zu, diese Materialien in Übereinstimmung mit unseren Nutzungsbedingungen und Datenschutzrichtlinien zu verwenden.",
          features: {
            gdpr: "DSGVO-konform",
            multilingual: "Mehrsprachiger Support",
            updated: "Regelmäßig aktualisiert"
          }
        }
      }
    },
    fr: {
      meta: {
        title: "Contact REXFEL - Portail de Partenariat | Société d\'Investissement Stratégique Hambourg",
        description: "Connectez-vous avec l'équipe d'investissement de REXFEL. Planifiez des réunions, téléchargez des ressources de partenariat et soumettez des demandes d'investissement. Société d'investissement stratégique basée à Hambourg avec une portée mondiale."
      },
      hero: {
        badge: "Portail de Partenariat",
        title: "Construisons l\'Avenir Ensemble",
        description: "Connectez-vous avec notre équipe d'investissement pour explorer les partenariats stratégiques, discuter des opportunités de financement et accéder aux ressources exclusives pour développer votre entreprise.",
        stats: {
          response: "Temps de Réponse",
          languages: "Langues",
          invested: "Investi"
        },
        cta: {
          schedule: "Planifier une Réunion",
          download: "Télécharger les Ressources"
        },
        cards: {
          partnership: {
            title: "Partenariat Stratégique",
            subtitle: "Opportunités de collaboration à long terme"
          },
          investment: {
            title: "Demande d\'Investissement",
            subtitle: "Financement et capital de croissance"
          },
          general: {
            title: "Demande Générale",
            subtitle: "Questions et informations"
          }
        }
      },
      form: {
        title: "Formulaire de Demande de Partenariat",
        subtitle: "Parlez-nous de votre entreprise et de la façon dont nous pouvons vous aider à grandir",
        fields: {
          inquiryType: {
            label: "Type de Demande",
            description: "Sélectionnez l\'option la plus pertinente pour votre demande",
            placeholder: "Choisir le type de demande..."
          },
          firstName: {
            label: "Prénom",
            placeholder: "Entrez votre prénom"
          },
          lastName: {
            label: "Nom de Famille",
            placeholder: "Entrez votre nom de famille"
          },
          email: {
            label: "Adresse E-mail",
            placeholder: "votre.email@entreprise.fr"
          },
          company: {
            label: "Nom de l\'Entreprise",
            placeholder: "Nom de votre entreprise"
          },
          position: {
            label: "Poste/Titre",
            placeholder: "PDG, Fondateur, etc."
          },
          phone: {
            label: "Numéro de Téléphone",
            placeholder: "+33 1 23 45 67 89"
          },
          fundingStage: {
            label: "Phase de Financement",
            placeholder: "Sélectionner la phase actuelle..."
          },
          industry: {
            label: "Secteur d\'Activité",
            placeholder: "Sélectionner le secteur..."
          },
          investmentAmount: {
            label: "Montant d\'Investissement Recherché",
            placeholder: "€500K - €5M",
            description: "Montant approximatif de financement que vous recherchez"
          },
          businessPlan: {
            label: "Plan d\'Affaires / Pitch Deck",
            placeholder: "Cliquez pour télécharger ou glisser-déposer",
            description: "PDF, DOC, PPT jusqu'à 10MB"
          },
          message: {
            label: "Message",
            placeholder: "Parlez-nous de votre entreprise, de vos plans de croissance et de la façon dont REXFEL peut vous aider à réussir..."
          },
          newsletter: {
            label: "S\'abonner à la newsletter REXFEL pour les insights du marché et les mises à jour d\'investissement"
          },
          gdprConsent: {
            label: "J'accepte le traitement de mes données personnelles selon la Politique de Confidentialité de REXFEL et les réglementations RGPD"
          }
        },
        inquiryTypes: {
          partnership: "Partenariat Stratégique",
          investment: "Demande d\'Investissement",
          general: "Demande Générale",
          media: "Médias & Presse"
        },
        fundingStages: {
          preSeed: "Pré-Amorçage",
          seed: "Amorçage",
          seriesA: "Série A",
          seriesB: "Série B+",
          growth: "Phase de Croissance"
        },
        industries: {
          other: "Autre"
        },
        investmentSection: {
          title: "Détails de l\'Investissement"
        },
        submit: "Envoyer la Demande",
        submitting: "Envoi en cours...",
        success: {
          title: "Merci pour Votre Demande !",
          message: "Nous avons reçu votre demande de partenariat et l\'examinerons attentivement. Notre équipe répondra dans les 24 heures.",
          nextSteps: {
            title: "Prochaines Étapes :",
            description: "1. Notre équipe d'investissement examinera votre soumission\n2. Nous planifierons un appel de consultation initial\n3. S'il y a un intérêt mutuel, nous organiserons une présentation détaillée"
          }
        }
      },
      team: {
        title: "Rencontrez Notre Équipe d\'Investissement",
        subtitle: "Connectez-vous directement avec nos professionnels expérimentés spécialisés dans les investissements stratégiques et le développement de partenariats.",
        members: {
          marcus: {
            position: "Associé Directeur",
            department: "Stratégie d\'Investissement",
            expertise: "20+ années en capital-risque et investissements stratégiques sur les marchés européens. Spécialisé en FinTech, SaaS et initiatives de transformation numérique.",
            bio: "Dr. Marcus Weber dirige la stratégie d'investissement de REXFEL avec plus de deux décennies d'expérience en capital-risque. Il a guidé avec succès 50+ entreprises à travers les phases de croissance, avec un focus particulier sur les ventures FinTech et SaaS européennes. Marcus détient un PhD en Économie de l'Université de Hambourg et a précédemment servi comme Associé dans des firmes VC européennes de premier plan.",
            availability: "Disponible pour des réunions"
          },
          sarah: {
            position: "Directrice d\'Investissement",
            department: "Due Diligence & Analyse",
            expertise: "Experte en modélisation financière, analyse de marché et due diligence d'investissement. Ancienne consultante McKinsey avec une expertise approfondie en modèles d'affaires numériques.",
            bio: "Sarah Chen apporte une rigueur analytique au processus d'investissement de REXFEL. Avec son expérience chez McKinsey & Company et en banque d'investissement, elle dirige nos efforts de due diligence et d'analyse financière. Sarah a évalué plus de 200 opportunités d'investissement et a un historique prouvé d'identification d'entreprises à fort potentiel de croissance.",
            availability: "Disponible cette semaine"
          },
          thomas: {
            position: "Directeur de Partenariat",
            department: "Développement Commercial",
            expertise: "Spécialisé en partenariats stratégiques, développement commercial et expansion de marché international. Ancien fondateur de startup avec expérience de sortie réussie.",
            bio: "Thomas Müller combine l'expérience entrepreneuriale avec l'expertise en partenariats stratégiques. En tant qu'ancien fondateur de startup qui a vendu avec succès sa plateforme e-commerce, il comprend les défis auxquels font face les entrepreneurs. Thomas aide maintenant les entreprises du portefeuille REXFEL à se développer grâce aux partenariats stratégiques et aux initiatives d'expansion de marché.",
            availability: "Disponible pour des appels"
          },
          elena: {
            position: "Directrice Juridique & Conformité",
            department: "Affaires Juridiques",
            expertise: "Droit des sociétés international, conformité réglementaire et structuration d'investissement. Spécialisée en réglementations d'investissement européennes et transactions transfrontalières.",
            bio: "Elena Rodriguez s'assure que tous les investissements REXFEL respectent les réglementations européennes et les standards internationaux. Avec ses capacités multilingues et sa compréhension approfondie du droit d'investissement transfrontalier, elle facilite les processus de transaction fluides et la conformité continue pour nos entreprises de portefeuille.",
            availability: "Disponible la semaine prochaine"
          }
        },
        actions: {
          email: "Envoyer un E-mail",
          schedule: "Planifier une Réunion",
          viewDetails: "Voir les Détails"
        },
        modal: {
          bio: "Biographie",
          expertise: "Expertise",
          contact: "Informations de Contact"
        }
      },
      office: {
        title: "Visitez Notre Bureau de Hambourg",
        subtitle: "Situé au cœur du quartier financier de Hambourg, notre bureau est facilement accessible et équipé pour des réunions productives.",
        tabs: {
          map: "Carte de Localisation",
          directions: "Directions"
        },
        address: {
          title: "Adresse du Bureau"
        },
        contact: {
          title: "Informations de Contact",
          main: "Bureau Principal",
          general: "Demandes Générales",
          reception: "Réception"
        },
        hours: {
          title: "Heures de Bureau",
          monday: "Lundi",
          tuesday: "Mardi",
          wednesday: "Mercredi",
          thursday: "Jeudi",
          friday: "Vendredi",
          saturday: "Samedi",
          sunday: "Dimanche",
          closed: "Fermé"
        },
        status: {
          open: "Actuellement Ouvert"
        },
        transportation: {
          title: "Transport Public",
          parking: "Parking Disponible"
        },
        actions: {
          getDirections: "Obtenir les Directions"
        },
        visit: {
          title: "Planifiez une Visite ?",
          description: "Veuillez prendre rendez-vous à l\'avance. Notre équipe de réception vous assistera avec l\'accès visiteur et les arrangements de salle de réunion."
        }
      },
      resources: {
        title: "Ressources de Partenariat",
        subtitle: "Téléchargez des guides complets, des formulaires de candidature et des insights de marché pour vous aider à vous préparer au partenariat avec REXFEL.",
        search: {
          placeholder: "Rechercher des ressources..."
        },
        categories: {
          all: "Toutes les Ressources",
          partnership: "Partenariat",
          investment: "Investissement",
          application: "Candidature",
          research: "Recherche"
        },
        items: {
          partnershipGuide: {
            title: "Directives de Partenariat 2024",
            description: "Guide complet couvrant les critères de partenariat de REXFEL, le processus de candidature et ce à quoi s'attendre pendant la phase d'évaluation."
          },
          investmentCriteria: {
            title: "Critères d\'Investissement & Domaines de Focus",
            description: "Aperçu détaillé de notre thèse d'investissement, industries cibles, phases de financement et critères d'évaluation pour les entreprises de portefeuille potentielles."
          },
          applicationForm: {
            title: "Modèle de Candidature de Partenariat",
            description: "Formulaire de candidature structuré pour vous aider à présenter votre opportunité d\'affaires dans le format que notre équipe d\'investissement préfère."
          },
          marketReport: {
            title: "Rapport de Marché FinTech Européen 2024",
            description: "Analyse approfondie du paysage FinTech européen, tendances émergentes et opportunités d'investissement dans le secteur de la finance numérique."
          },
          dueDiligence: {
            title: "Liste de Contrôle Due Diligence",
            description: "Liste complète des documents et informations requis pendant le processus de due diligence pour rationaliser votre candidature."
          },
          portfolioOverview: {
            title: "Histoires de Succès des Entreprises de Portefeuille",
            description: "Études de cas et histoires de succès de nos entreprises de portefeuille, montrant les trajectoires de croissance et la création de valeur stratégique."
          }
        },
        download: "Télécharger",
        downloading: "Téléchargement...",
        downloads: "téléchargements",
        downloadSuccess: "téléchargé avec succès !",
        lastUpdated: "Dernière mise à jour",
        noResults: {
          title: "Aucune Ressource Trouvée",
          description: "Essayez d\'ajuster vos termes de recherche ou filtre de catégorie.",
          reset: "Réinitialiser les Filtres"
        },
        notice: {
          title: "Avis d\'Accès aux Ressources",
          description: "Toutes les ressources sont fournies à des fins d'information. En téléchargeant, vous acceptez d'utiliser ces matériaux conformément à nos conditions de service et politique de confidentialité.",
          features: {
            gdpr: "Conforme RGPD",
            multilingual: "Support Multilingue",
            updated: "Régulièrement Mis à Jour"
          }
        }
      }
    },
    es: {
      meta: {
        title: "Contacto REXFEL - Portal de Asociación | Firma de Inversión Estratégica Hamburgo",
        description: "Conéctese con el equipo de inversión de REXFEL. Programe reuniones, descargue recursos de asociación y envíe consultas de inversión. Firma de inversión estratégica con sede en Hamburgo con alcance global."
      },
      hero: {
        badge: "Portal de Asociación",
        title: "Construyamos el Futuro Juntos",
        description: "Conéctese con nuestro equipo de inversión para explorar asociaciones estratégicas, discutir oportunidades de financiamiento y acceder a recursos exclusivos para escalar su negocio.",
        stats: {
          response: "Tiempo de Respuesta",
          languages: "Idiomas",
          invested: "Invertido"
        },
        cta: {
          schedule: "Programar Reunión",
          download: "Descargar Recursos"
        },
        cards: {
          partnership: {
            title: "Asociación Estratégica",
            subtitle: "Oportunidades de colaboración a largo plazo"
          },
          investment: {
            title: "Consulta de Inversión",
            subtitle: "Financiamiento y capital de crecimiento"
          },
          general: {
            title: "Consulta General",
            subtitle: "Preguntas e información"
          }
        }
      },
      form: {
        title: "Formulario de Consulta de Asociación",
        subtitle: "Cuéntenos sobre su negocio y cómo podemos ayudarle a crecer",
        fields: {
          inquiryType: {
            label: "Tipo de Consulta",
            description: "Seleccione la opción más relevante para su solicitud",
            placeholder: "Elegir tipo de consulta..."
          },
          firstName: {
            label: "Nombre",
            placeholder: "Ingrese su nombre"
          },
          lastName: {
            label: "Apellido",
            placeholder: "Ingrese su apellido"
          },
          email: {
            label: "Dirección de Correo Electrónico",
            placeholder: "su.email@empresa.es"
          },
          company: {
            label: "Nombre de la Empresa",
            placeholder: "Nombre de su empresa"
          },
          position: {
            label: "Posición/Título",
            placeholder: "CEO, Fundador, etc."
          },
          phone: {
            label: "Número de Teléfono",
            placeholder: "+34 91 123 45 67"
          },
          fundingStage: {
            label: "Etapa de Financiamiento",
            placeholder: "Seleccionar etapa actual..."
          },
          industry: {
            label: "Sector Industrial",
            placeholder: "Seleccionar industria..."
          },
          investmentAmount: {
            label: "Cantidad de Inversión Buscada",
            placeholder: "€500K - €5M",
            description: "Cantidad aproximada de financiamiento que está buscando"
          },
          businessPlan: {
            label: "Plan de Negocios / Pitch Deck",
            placeholder: "Haga clic para subir o arrastrar y soltar",
            description: "PDF, DOC, PPT hasta 10MB"
          },
          message: {
            label: "Mensaje",
            placeholder: "Cuéntenos sobre su negocio, planes de crecimiento y cómo REXFEL puede ayudarle a tener éxito..."
          },
          newsletter: {
            label: "Suscribirse al boletín de REXFEL para insights del mercado y actualizaciones de inversión"
          },
          gdprConsent: {
            label: "Acepto el procesamiento de mis datos personales según la Política de Privacidad de REXFEL y las regulaciones GDPR"
          }
        },
        inquiryTypes: {
          partnership: "Asociación Estratégica",
          investment: "Consulta de Inversión",
          general: "Consulta General",
          media: "Medios y Prensa"
        },
        fundingStages: {
          preSeed: "Pre-Semilla",
          seed: "Semilla",
          seriesA: "Serie A",
          seriesB: "Serie B+",
          growth: "Etapa de Crecimiento"
        },
        industries: {
          other: "Otro"
        },
        investmentSection: {
          title: "Detalles de Inversión"
        },
        submit: "Enviar Consulta",
        submitting: "Enviando...",
        success: {
          title: "¡Gracias por Su Consulta!",
          message: "Hemos recibido su solicitud de asociación y la revisaremos cuidadosamente. Nuestro equipo responderá dentro de 24 horas.",
          nextSteps: {
            title: "Próximos Pasos:",
            description: "1. Nuestro equipo de inversión revisará su envío\n2. Programaremos una llamada de consulta inicial\n3. Si hay interés mutuo, organizaremos una presentación detallada"
          }
        }
      },
      team: {
        title: "Conozca Nuestro Equipo de Inversión",
        subtitle: "Conéctese directamente con nuestros profesionales experimentados especializados en inversiones estratégicas y desarrollo de asociaciones.",
        members: {
          marcus: {
            position: "Socio Gerente",
            department: "Estrategia de Inversión",
            expertise: "20+ años en capital de riesgo e inversiones estratégicas en mercados europeos. Especializado en FinTech, SaaS e iniciativas de transformación digital.",
            bio: "Dr. Marcus Weber lidera la estrategia de inversión de REXFEL con más de dos décadas de experiencia en capital de riesgo. Ha guiado exitosamente a 50+ empresas a través de fases de crecimiento, con un enfoque particular en ventures FinTech y SaaS europeas. Marcus tiene un PhD en Economía de la Universidad de Hamburgo y anteriormente sirvió como Socio en firmas VC europeas líderes.",
            availability: "Disponible para reuniones"
          },
          sarah: {
            position: "Directora de Inversión",
            department: "Due Diligence y Análisis",
            expertise: "Experta en modelado financiero, análisis de mercado y due diligence de inversión. Ex consultora de McKinsey con profunda experiencia en modelos de negocio digitales.",
            bio: "Sarah Chen aporta rigor analítico al proceso de inversión de REXFEL. Con su experiencia en McKinsey & Company y experiencia en banca de inversión, lidera nuestros esfuerzos de due diligence y análisis financiero. Sarah ha evaluado más de 200 oportunidades de inversión y tiene un historial comprobado de identificar empresas con alto potencial de crecimiento.",
            availability: "Disponible esta semana"
          },
          thomas: {
            position: "Director de Asociaciones",
            department: "Desarrollo de Negocios",
            expertise: "Especializado en asociaciones estratégicas, desarrollo de negocios y expansión de mercado internacional. Ex fundador de startup con experiencia exitosa de salida.",
            bio: "Thomas Müller combina experiencia empresarial con expertise en asociaciones estratégicas. Como ex fundador de startup que vendió exitosamente su plataforma de e-commerce, entiende los desafíos que enfrentan los emprendedores. Thomas ahora ayuda a las empresas del portafolio de REXFEL a escalar a través de asociaciones estratégicas e iniciativas de expansión de mercado.",
            availability: "Disponible para llamadas"
          },
          elena: {
            position: "Directora Legal y Cumplimiento",
            department: "Asuntos Legales",
            expertise: "Derecho corporativo internacional, cumplimiento regulatorio y estructuración de inversiones. Especializada en regulaciones de inversión europeas y transacciones transfronterizas.",
            bio: "Elena Rodriguez asegura que todas las inversiones de REXFEL cumplan con las regulaciones europeas y estándares internacionales. Con sus capacidades multilingües y profundo entendimiento del derecho de inversión transfronterizo, facilita procesos de transacción fluidos y cumplimiento continuo para nuestras empresas de portafolio.",
            availability: "Disponible la próxima semana"
          }
        },
        actions: {
          email: "Enviar Correo",
          schedule: "Programar Reunión",
          viewDetails: "Ver Detalles"
        },
        modal: {
          bio: "Biografía",
          expertise: "Experiencia",
          contact: "Información de Contacto"
        }
      },
      office: {
        title: "Visite Nuestra Oficina de Hamburgo",
        subtitle: "Ubicada en el corazón del distrito financiero de Hamburgo, nuestra oficina es fácilmente accesible y está equipada para reuniones productivas.",
        tabs: {
          map: "Mapa de Ubicación",
          directions: "Direcciones"
        },
        address: {
          title: "Dirección de la Oficina"
        },
        contact: {
          title: "Información de Contacto",
          main: "Oficina Principal",
          general: "Consultas Generales",
          reception: "Recepción"
        },
        hours: {
          title: "Horarios de Oficina",
          monday: "Lunes",
          tuesday: "Martes",
          wednesday: "Miércoles",
          thursday: "Jueves",
          friday: "Viernes",
          saturday: "Sábado",
          sunday: "Domingo",
          closed: "Cerrado"
        },
        status: {
          open: "Actualmente Abierto"
        },
        transportation: {
          title: "Transporte Público",
          parking: "Estacionamiento Disponible"
        },
        actions: {
          getDirections: "Obtener Direcciones"
        },
        visit: {
          title: "¿Planificando una Visita?",
          description: "Por favor programe una cita con anticipación. Nuestro equipo de recepción le ayudará con el acceso de visitantes y arreglos de sala de reuniones."
        }
      },
      resources: {
        title: "Recursos de Asociación",
        subtitle: "Descargue guías completas, formularios de aplicación e insights de mercado para ayudarle a prepararse para la asociación con REXFEL.",
        search: {
          placeholder: "Buscar recursos..."
        },
        categories: {
          all: "Todos los Recursos",
          partnership: "Asociación",
          investment: "Inversión",
          application: "Aplicación",
          research: "Investigación"
        },
        items: {
          partnershipGuide: {
            title: "Directrices de Asociación 2024",
            description: "Guía completa que cubre los criterios de asociación de REXFEL, proceso de aplicación y qué esperar durante la fase de evaluación."
          },
          investmentCriteria: {
            title: "Criterios de Inversión y Áreas de Enfoque",
            description: "Visión detallada de nuestra tesis de inversión, industrias objetivo, etapas de financiamiento y criterios de evaluación para empresas de portafolio potenciales."
          },
          applicationForm: {
            title: "Plantilla de Aplicación de Asociación",
            description: "Formulario de aplicación estructurado para ayudarle a presentar su oportunidad de negocio en el formato que nuestro equipo de inversión prefiere."
          },
          marketReport: {
            title: "Reporte de Mercado FinTech Europeo 2024",
            description: "Análisis profundo del panorama FinTech europeo, tendencias emergentes y oportunidades de inversión en el sector de finanzas digitales."
          },
          dueDiligence: {
            title: "Lista de Verificación Due Diligence",
            description: "Lista completa de documentos e información requerida durante el proceso de due diligence para agilizar su aplicación."
          },
          portfolioOverview: {
            title: "Historias de Éxito de Empresas de Portafolio",
            description: "Estudios de caso e historias de éxito de nuestras empresas de portafolio, mostrando trayectorias de crecimiento y creación de valor estratégico."
          }
        },
        download: "Descargar",
        downloading: "Descargando...",
        downloads: "descargas",
        downloadSuccess: "¡descargado exitosamente!",
        lastUpdated: "Última actualización",
        noResults: {
          title: "No se Encontraron Recursos",
          description: "Intente ajustar sus términos de búsqueda o filtro de categoría.",
          reset: "Restablecer Filtros"
        },
        notice: {
          title: "Aviso de Acceso a Recursos",
          description: "Todos los recursos se proporcionan con fines informativos. Al descargar, acepta usar estos materiales de acuerdo con nuestros términos de servicio y política de privacidad.",
          features: {
            gdpr: "Cumple con GDPR",
            multilingual: "Soporte Multilingüe",
            updated: "Actualizado Regularmente"
          }
        }
      }
    }
  };

  const currentTranslations = translations?.[currentLanguage] || translations?.en;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{currentTranslations?.meta?.title}</title>
        <meta name="description" content={currentTranslations?.meta?.description} />
        <meta property="og:title" content={currentTranslations?.meta?.title} />
        <meta property="og:description" content={currentTranslations?.meta?.description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentTranslations?.meta?.title} />
        <meta name="twitter:description" content={currentTranslations?.meta?.description} />
      </Helmet>
      <Header />
      {/* Language Switcher - Fixed Position */}
      <div className="fixed top-20 right-6 z-40">
        <LanguageSwitcher 
          currentLanguage={currentLanguage}
          onLanguageChange={handleLanguageChange}
        />
      </div>
      {/* Hero Section */}
      <ContactHero 
        currentLanguage={currentLanguage}
        translations={currentTranslations}
      />
      {/* Main Content */}
      <div className="relative">
        {/* Contact Form Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <ContactForm 
              currentLanguage={currentLanguage}
              translations={currentTranslations}
            />
          </div>
        </section>

        {/* Team Directory */}
        <TeamDirectory 
          currentLanguage={currentLanguage}
          translations={currentTranslations}
        />

        {/* Office Location */}
        <OfficeLocation 
          currentLanguage={currentLanguage}
          translations={currentTranslations}
        />

        {/* Resource Downloads */}
        <ResourceDownloads 
          currentLanguage={currentLanguage}
          translations={currentTranslations}
        />

        {/* Contact CTA Section */}
        <section className="py-16 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="mb-8">
              <Icon name="Rocket" size={48} className="mx-auto mb-6 text-blue-200" />
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                {currentLanguage === 'en' && "Ready to Scale Your Business?"}
                {currentLanguage === 'de' && "Bereit, Ihr Unternehmen zu skalieren?"}
                {currentLanguage === 'fr' && "Prêt à Développer Votre Entreprise ?"}
                {currentLanguage === 'es' && "¿Listo para Escalar Su Negocio?"}
              </h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                {currentLanguage === 'en' && "Join the REXFEL portfolio and accelerate your growth with strategic investment and operational expertise."}
                {currentLanguage === 'de' && "Treten Sie dem REXFEL-Portfolio bei und beschleunigen Sie Ihr Wachstum mit strategischen Investitionen und operativer Expertise."}
                {currentLanguage === 'fr' && "Rejoignez le portefeuille REXFEL et accélérez votre croissance avec des investissements stratégiques et une expertise opérationnelle."}
                {currentLanguage === 'es' && "Únase al portafolio de REXFEL y acelere su crecimiento con inversión estratégica y experiencia operacional."}
              </p>
            </div>

            {/* Contact Stats */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">€0.8M+</div>
                <div className="text-blue-200">
                  {currentLanguage === 'en' && "Total Investment"}
                  {currentLanguage === 'de' && "Gesamtinvestition"}
                  {currentLanguage === 'fr' && "Investissement Total"}
                  {currentLanguage === 'es' && "Inversión Total"}
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">8+</div>
                <div className="text-blue-200">
                  {currentLanguage === 'en' && "Portfolio Companies"}
                  {currentLanguage === 'de' && "Portfolio-Unternehmen"}
                  {currentLanguage === 'fr' && "Entreprises de Portefeuille"}
                  {currentLanguage === 'es' && "Empresas de Portafolio"}
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">5+</div>
                <div className="text-blue-200">
                  {currentLanguage === 'en' && "Languages Supported"}
                  {currentLanguage === 'de' && "Unterstützte Sprachen"}
                  {currentLanguage === 'fr' && "Langues Supportées"}
                  {currentLanguage === 'es' && "Idiomas Soportados"}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <span className="text-white font-bold text-lg font-mono">R</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">REXFEL</h3>
                  <p className="text-gray-400 text-sm">Strategic Investment Firm</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4 max-w-md">
                {currentLanguage === 'en' && "Hamburg-based strategic investment firm specializing in FinTech, SaaS, and digital transformation ventures across Europe."}
                {currentLanguage === 'de' && "Hamburg-basierte strategische Investmentfirma, spezialisiert auf FinTech, SaaS und digitale Transformationsvorhaben in ganz Europa."}
                {currentLanguage === 'fr' && "Société d'investissement stratégique basée à Hambourg, spécialisée dans les ventures FinTech, SaaS et de transformation numérique à travers l'Europe."}
                {currentLanguage === 'es' && "Firma de inversión estratégica con sede en Hamburgo, especializada en ventures FinTech, SaaS y transformación digital en toda Europa."}
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Icon name="Linkedin" size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Icon name="Twitter" size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Icon name="Mail" size={20} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">
                {currentLanguage === 'en' && "Quick Links"}
                {currentLanguage === 'de' && "Schnelllinks"}
                {currentLanguage === 'fr' && "Liens Rapides"}
                {currentLanguage === 'es' && "Enlaces Rápidos"}
              </h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="/about" className="hover:text-white transition-colors">
                  {currentLanguage === 'en' && "About Us"}
                  {currentLanguage === 'de' && "Über uns"}
                  {currentLanguage === 'fr' && "À Propos"}
                  {currentLanguage === 'es' && "Acerca de"}
                </a></li>
                <li><a href="/portfolio" className="hover:text-white transition-colors">Portfolio</a></li>
                <li><a href="/news" className="hover:text-white transition-colors">
                  {currentLanguage === 'en' && "News & Insights"}
                  {currentLanguage === 'de' && "News & Einblicke"}
                  {currentLanguage === 'fr' && "Actualités & Insights"}
                  {currentLanguage === 'es' && "Noticias e Insights"}
                </a></li>
                <li><a href="/legal-hub" className="hover:text-white transition-colors">
                  {currentLanguage === 'en' && "Legal"}
                  {currentLanguage === 'de' && "Rechtliches"}
                  {currentLanguage === 'fr' && "Légal"}
                  {currentLanguage === 'es' && "Legal"}
                </a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold mb-4">
                {currentLanguage === 'en' && "Contact"}
                {currentLanguage === 'de' && "Kontakt"}
                {currentLanguage === 'fr' && "Contact"}
                {currentLanguage === 'es' && "Contacto"}
              </h4>
              <div className="space-y-2 text-gray-300">
                <p className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} />
                  <span>Dietzweg 15, 22119 Hamburg</span>
                </p>
                <p className="flex items-center space-x-2">
                  <Icon name="Phone" size={16} />
                  <span>+49 172 7999621</span>
                </p>
                <p className="flex items-center space-x-2">
                  <Icon name="Mail" size={16} />
                  <span>info@rexfel.com</span>
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date()?.getFullYear()} REXFEL Technology and Innovation. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;